import type {
  LoggerContext,
  LoggerTransport,
  LogMessage,
  LOGGER_MODULE,
  LOG_LEVEL_NAME,
  LogLevel,
} from '@/services/logger/types';

import { VglError } from '@/services/errorHandler/vglError';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { LogLevelMapping } from '@/services/logger/types';

export class Logger {
  private readonly transports: Map<symbol, LoggerTransport>;

  protected readonly logLevelName: LOG_LEVEL_NAME;

  protected context: LoggerContext;

  constructor(
    levelName: LOG_LEVEL_NAME,
    context: LoggerContext,
    transports: Map<symbol, LoggerTransport>,
  ) {
    this.logLevelName = levelName;
    this.context = context;
    this.transports = transports;
  }

  public updateContext(context: LoggerContext): void {
    this.context = context;
  }

  public log(
    levelName: LOG_LEVEL_NAME,
    module: LOGGER_MODULE,
    ...msg: unknown[]
  ): void {
    const indexGlobalLogLevel = LogLevelMapping[this.logLevelName];
    const indexLogLevel = LogLevelMapping[levelName];

    if (indexLogLevel > indexGlobalLogLevel) return;
    this.handleLog(levelName, indexLogLevel, module, msg);
  }

  private handleLog(
    levelName: LOG_LEVEL_NAME,
    level: LogLevel,
    module: LOGGER_MODULE,
    msg: unknown[],
  ): void {
    const parsedLogMessage: (string | Error)[] = [];

    msg.forEach((part) => {
      if (part === null || part === undefined) return;

      if (Array.isArray(part)) {
        parsedLogMessage.push(part.join(' / '));
      } else if (typeof part === 'object' && !(part instanceof Error)) {
        parsedLogMessage.push(JSON.stringify(part));
      } else if (typeof part === 'string' || part instanceof Error) {
        parsedLogMessage.push(part);
      } else {
        throw new VglError(VGL_ERROR_CODE.LOGGER_PARSING_FAILED, {
          additionalData: {
            part,
          },
        });
      }
    });

    const ts = Date.now();

    const logMessage: LogMessage = {
      message: `(${module}) ${parsedLogMessage.join(' / ')}`,
      context: this.context,
      module,
      levelName,
      level,
      ts,
    };

    this.transports.forEach((transport) => {
      transport.handle(logMessage);
    });
  }
}
